<template>
  <div
    class="w-full relative restaurant-card"
    :class="{
      'restaurant-card--horizontal': horizontal,
      'restaurant-card--vertical': !horizontal
    }"
  >
    <div
      @click="showRestaurantDetail(item)"
      class="relative w-full h-full top-0 left-0 rounded-3xl overflow-hidden restaurant-card__link"
      :class="{
        shimmer: item.shimmer
      }"
    >
      <div class="relative" :class="horizontal ? 'p-[35%]' : 'aspect-[4/3]'">
        <div
          class="rounded-3xl bg-slate-100 overflow-hidden w-full h-full absolute top-0 left-0 flex items-center flex-col justify-center"
        >
          <div
            v-if="item.shimmer"
            class="absolute w-full h-full duration-300 shimmer-animation"
          ></div>

          <!-- Placeholder image -->
          <ImgComponent
            class="absolute w-full h-full scale-[1.2] duration-300 overflow-auto object-contain bg-[#FAFAFA]"
            :src="placeholderImage"
            alt=""
            :class="{ 'opacity-0': !showPlaceholder }"
          />

          <!-- Main restaurant image -->
          <ImgComponent
            v-if="!item.shimmer && item.image"
            class="absolute max-w-[100000px] w-[200%] h-[200%] object-cover scale-[0.6]"
            :src="item.image"
            @load="handleImageLoad"
            @error="handleImageError"
            alt=""
          />

          <!-- Rating badge -->
          <div
            v-if="!item.shimmer && item.rating > 0"
            class="absolute top-3 right-14 bg-white px-2 py-1 rounded-full flex items-center gap-1 shadow-md z-[1]"
          >
            <ImgComponent
              src="/images/all/star.svg"
              class="w-4 h-4 text-yellow-400"
              alt="Note"
            />
            <span class="text-sm font-bold">{{ item.rating }}</span>
          </div>

          <!-- Favorite Icon -->
          <div
            v-if="!item.shimmer && item.id"
            class="absolute w-10 h-10 right-2 top-2 rounded-full ripple z-[2]"
            @click.stop="toggleFavorite"
          >
            <div
              class="w-full h-full rounded-full border-white border-[6px] bg-white p-1 shadow-sm"
            >
              <ImgComponent
                class="w-full h-full"
                :src="
                  isFavorite
                    ? '/images/all/heart-primary.svg'
                    : '/images/all/heart-outline.svg'
                "
                alt=""
              />
            </div>
          </div>

          <!-- Top left badges (Closed & Promo) -->
          <div class="absolute top-3 left-3 flex flex-col gap-1 items-start z-[2]">
            <!-- Closed badge -->
            <div
              v-if="!item.shimmer && !isOpen"
              class="bg-red-500 text-white px-2.5 py-1 rounded-full text-[10px] font-bold shadow-md uppercase tracking-wider"
            >
              Fermé
            </div>

            <!-- Promo badge -->
            <div
              v-if="!item.shimmer && item.hasPromotion"
              class="bg-primary text-white px-3 py-1 rounded-full text-[10px] font-bold shadow-lg shadow-primary/30"
            >
              <div class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z"
                    clip-rule="evenodd"
                  />
                  <path
                    d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z"
                  />
                </svg>
                <span>{{ item.promotionSummary || 'PROMO' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Restaurant info overlay -->
      <div
        v-if="!item.shimmer"
        class="absolute bottom-0 glass-effect w-full rounded-b-3xl"
      >
        <div class="w-full flex p-4 pb-2 items-center gap-3">
          <!-- Restaurant logo -->
          <div
            class="w-12 h-12 bg-slate-100 min-w-12 rounded-full relative flex items-center justify-center overflow-hidden shadow-md"
          >
            <ImgComponent
              v-if="item.logo"
              class="absolute max-w-[100000px] w-[200%] h-[200%] object-cover scale-[0.6]"
              :src="item.logo"
              @load="handleImageLoad"
              @error="handleImageError"
              alt=""
            />
            <div
              v-else
              class="w-full h-full bg-white flex items-center justify-center"
            >
              <span class="text-primary-600 text-3xl font-bold uppercase font-ribeye">
                {{ (item.storeName || 'R').charAt(0) }}
              </span>
            </div>
          </div>

          <!-- Restaurant details -->
          <div class="flex flex-col w-full min-w-0">
            <!-- Restaurant name -->
            <div class="relative w-full title-color">
              <h4 class="line-clamp-1 font-bold text-base">
                {{ item.storeName || 'Restaurant' }}
              </h4>
            </div>

            <!-- Delivery info -->
            <div class="flex gap-2 items-center mt-1">
              <div
                v-if="!isOpen"
                class="text-red-500 text-xs font-semibold"
              >
                Fermé actuellement
              </div>
              <div
                v-else-if="item.deliveryFee === 0 || item.freeDelivery"
                class="text-primary-600 text-xs font-bold"
              >
                Livraison gratuite
              </div>
              <div v-else-if="item.deliveryFee" class="text-gray-600 text-xs">
                Livraison: {{ item.deliveryFee }} FCFA
              </div>
            </div>

            <!-- Rating stars -->
            <div  class="flex gap-0.5 mt-1" :class="{'opacity-50':!item.rating}">
              <i
                v-for="star in 5"
                :key="star"
                class="text-xs"
                :class="{
                  'fas fa-star text-yellow-400':
                    star <= (item.ratingStars || item.rating || 0),
                  'far fa-star text-yellow-400/40':
                    star > (item.ratingStars || item.rating || 0)
                }"
              ></i>
            </div>
          </div>
        </div>

        <!-- Categories tags -->
        <div
          v-if="item.categories && item.categories.length > 0"
          class="w-full p-4 pt-0"
        >
          <div
            class="bg-primary-50 px-3 py-1.5 rounded-xl text-primary-700 inline-block whitespace-nowrap mb-2 mr-2 text-xs font-medium"
            v-for="(category, i) in item.categories.slice(0, 3)"
            :key="i"
          >
            {{ category?.name || category || 'Category' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RestaurantCard',

  props: {
    item: {
      type: Object,
      default() {
        return {};
      }
    },
    horizontal: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showPlaceholder: true
    };
  },

  computed: {
    placeholderImage() {
      return '/images/restaurant.webp';
      return '/images/placeholders/restaurant-dishes.png';
    },
    isFavorite() {
      const favoriteRestaurants =
        window.$stores?.user?.state?.current?.favoriteRestaurants || [];
      return favoriteRestaurants.includes(this.item.id);
    },
    isOpen() {
      if (this.item.isOpen === false) return false;
      if (this.item.isWithinOpeningHours === false) return false;
      return true;
    }
  },

  mounted() {
    setTimeout(() => {
      this.showPlaceholder = true;
    }, 5000);
  },

  methods: {
    showRestaurantDetail(restaurant) {
      if (!restaurant || !restaurant.id) return;
      this.$bus.emit('restaurant.SHOW_RESTAURANT_DETAIL', {
        id: restaurant.id
      });
    },

    toggleFavorite() {
      if (!this.item?.id) return;
      this.$bus.emit(window.$constants.EVENTS.USER.FAVORITE_TOGGLE_REST, {
        restaurantId: this.item.id,
        isFavorite: this.isFavorite
      });
    },

    handleImageLoad(event) {
      event.target.style.backgroundColor = 'white';
    },

    handleImageError(event) {
      event.target.style.display = 'none';
    }
  }
};
</script>

<style scoped>
.restaurant-card {
  transition: transform 0.2s ease;
}

.restaurant-card:hover {
  transform: translateY(-2px);
}

.restaurant-card__link {
  display: block;
  text-decoration: none;
  color: inherit;
}

/* Glass effect for overlay */
.glass-effect {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}

/* Shimmer animation */
.shimmer {
  pointer-events: none;
}

.shimmer-animation {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
/* Line clamp for text truncation */
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.title-color {
  color: #1f2937;
}
</style>
